// Platform rule (Veronika, 2026-08-26, reaffirmed 2026-09-16): EVERY file download requires a
// logged-in member — downloads are vendor leads. This is the single definition of "a file
// link" shared by the rich-text renderer (UI gate), the middleware (deep-link gate) and the
// full-qa integrity check (regression gate). Extend the extension list here, nowhere else.
export const GATED_FILE_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|zip|csv)$/i

// Only OUR files are gated: host-relative links, anything on an electrification-academy.com
// host, and the ea-media Blob store. A citation that points at a third party's public
// document (e.g. a US OSHA guide on osha.gov, referenced from the recycling regulations
// band) is a reference, not a download we hand out — it stays a plain link.
const FIRST_PARTY_HOST_RE = /(^|\.)electrification-academy\.com$|\.public\.blob\.vercel-storage\.com$/i
const PLACEHOLDER_ORIGIN = 'https://internal.invalid'

/** true when a link target is one of OUR files (absolute or host-relative, query/hash allowed) */
export function isGatedFileUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || !url) return false
  try {
    const u = new URL(url, PLACEHOLDER_ORIGIN)
    const firstParty = u.origin === PLACEHOLDER_ORIGIN || FIRST_PARTY_HOST_RE.test(u.hostname)
    return firstParty && GATED_FILE_EXT_RE.test(u.pathname)
  } catch {
    return false
  }
}

// Files that travel through OUR middleware gate: host-relative links and anything on an
// electrification-academy.com host. Blob-store links are not cookie-gated at the network
// level, so they need no pass.
const SITE_HOST_RE = /(^|\.)electrification-academy\.com$/i

/** true when a file link is served through the middleware login gate (see filePass.ts) */
export function needsFilePass(url: string | null | undefined): boolean {
  if (!isGatedFileUrl(url)) return false
  const u = new URL(url as string, PLACEHOLDER_ORIGIN)
  return u.origin === PLACEHOLDER_ORIGIN || SITE_HOST_RE.test(u.hostname)
}

/** a member's file link with their file pass attached, when the link needs one */
export function withFilePass(url: string, pass: string | null): string {
  if (!pass || !needsFilePass(url)) return url
  const hashAt = url.indexOf('#')
  const base = hashAt === -1 ? url : url.slice(0, hashAt)
  const hash = hashAt === -1 ? '' : url.slice(hashAt)
  return `${base}${base.includes('?') ? '&' : '?'}pass=${pass}${hash}`
}

/** display/download filename for a file URL */
export function fileNameFromUrl(url: string): string {
  try {
    const last = new URL(url, 'https://internal.invalid').pathname.split('/').pop() || ''
    return decodeURIComponent(last) || 'download'
  } catch {
    return 'download'
  }
}
